import Box from "@mui/material/Box";
import { useEffect, useRef, useState, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useMediaQuery } from "@mui/material";
import CommonChatMainContainer from "../../../../components/CommonChat/CommonChatMainContainer";
import CommonChatMessageInput from "../../../../components/CommonChat/CommonChatMessageInput";
import {
  CHAT_CONFIGURATION_ADS_DETAILED_REPORT_ID,
  MIN_WIDTH_SCREEN_TO_SHOW_CHAT,
} from "../../../../utils/constants";
import { getRoleCapability } from "../../../../actions/getters";
import {
  CHAT_ASK_A_QUESTION_ABOUT_YOUR_CAMPAIGN,
  RESET_CHAT_ALERT_DESCRIPTION,
  RESET_CHAT_ALERT_TITLE,
} from "../../../../i18n/keysTranslations";
import { getFormattedCampaign } from "../../../../utils/string";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { X } from "lucide-react";
import IconButton from "../../../../components/Buttons/IconButton";
import { useDispatchApp } from "../../../../lib/redux";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import { startDeleteCampaignReport } from "../../../../actions/adsCampaigns";
import { ConfirmAlert } from "../../../../components/Alerts/Alerts";
import { useTranslationApp } from "../../../../lib/i18next";
import { useRebrandBanner } from "../../../../hooks/useRebrandBanner";
import { CampaignOpeningAssistProvider } from "../../context/CampaignOpeningAssistContext";
import {
  isCampaignOpeningAssistEligible,
  useCampaignOpeningAutoPrompt,
} from "../../hooks/useCampaignOpeningAutoPrompt";
import {
  OPENING_ASSIST_EMPTY_PLACEHOLDER,
  OPENING_ASSIST_HIGHLIGHT_MS,
} from "../../../../components/Chat/openingAssistConstants";

const SlideChat = ({
  isVisible = true,
  onCloseChat = () => {},
  onResetChat: onResetChatCallback = () => {},
  onRegisterClearPreloadedMessages = () => {},
}) => {
  const isLargeScreen = useMediaQuery(
    `(min-width: ${MIN_WIDTH_SCREEN_TO_SHOW_CHAT}px)`
  );
  const dispatch = useDispatchApp();
  const canUseCampaignAgent = dispatch(
    getRoleCapability("canUseCampaignAgent")
  );
  const { rebrandBannerOffsetPx, totalTopOffsetPx } = useRebrandBanner();
  const [resetChatKey, setResetChatKey] = useState(0);
  const [highlightMessageKey, setHighlightMessageKey] = useState(null);

  const {
    getValues,
    statistics,
    dateFiltersForm,
    onChangeOptimizations,
    chatMessages,
    onChangeChatMessages,
    shouldOpenChat,
    onChangeShouldOpenChat,
    isSyncingData,
  } = useFormContext();

  const [isOpenChat, setIsOpenChat] = useState(true);
  const chatSendHandlerRef = useRef(null);
  const chatActionsRef = useRef(null);
  const pendingOpeningHighlightRef = useRef(false);

  const { watch: watchDateRange } = dateFiltersForm || { watch: () => null };
  const dateRange = watchDateRange ? watchDateRange("dateRange") : null;
  const campaign = getValues();

  const { t } = useTranslationApp();

  const onExposeSendMessage = useCallback((handler) => {
    chatSendHandlerRef.current = handler;
  }, []);

  const onExposeChatActions = useCallback((api) => {
    chatActionsRef.current = api;
  }, []);

  const openingAutoPromptEligible = isCampaignOpeningAssistEligible({
    campaignId: campaign?.id,
    chatMessages,
    reportStates: campaign?.report?.states,
  });

  const { openingAssistConsumed } = useCampaignOpeningAutoPrompt({
    campaignId: campaign?.id,
    chatMessages,
    reportStates: campaign?.report?.states,
    isSyncingData,
    chatActionsRef,
    pendingOpeningHighlightRef,
    t,
  });

  const emptyChatPlaceholder =
    openingAutoPromptEligible && !openingAssistConsumed
      ? OPENING_ASSIST_EMPTY_PLACEHOLDER.ANALYZING
      : null;

  const onAgentReply = useCallback(({ messageKey }) => {
    if (!pendingOpeningHighlightRef.current) {
      return;
    }
    pendingOpeningHighlightRef.current = false;
    setHighlightMessageKey(messageKey);
  }, []);

  useEffect(() => {
    if (highlightMessageKey == null) {
      return undefined;
    }
    const id = window.setTimeout(() => {
      setHighlightMessageKey(null);
    }, OPENING_ASSIST_HIGHLIGHT_MS);
    return () => window.clearTimeout(id);
  }, [highlightMessageKey]);

  const onResetChat = async () => {
    const response = await ConfirmAlert({
      title: t(RESET_CHAT_ALERT_TITLE),
      text: t(RESET_CHAT_ALERT_DESCRIPTION),
    });

    if (!response.isConfirmed) return;

    pendingOpeningHighlightRef.current = false;
    setHighlightMessageKey(null);

    if (onChangeChatMessages) {
      onChangeChatMessages({});
    }

    onChangeOptimizations([]);

    const campaignID = campaign?.id;
    if (campaignID) {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (advertiserID) {
        await dispatch(
          startDeleteCampaignReport({
            campaignID,
          })
        );
      }
    }

    onResetChatCallback();

    setResetChatKey((prev) => prev + 1);
  };

  useEffect(() => {
    if (shouldOpenChat && Object.keys(chatMessages || {}).length > 0) {
      setIsOpenChat(true);
      onChangeShouldOpenChat(false);
    }
  }, [shouldOpenChat, chatMessages, onChangeShouldOpenChat]);

  if (!canUseCampaignAgent) return null;

  if (!campaign || Object.keys(campaign || {}).length === 0) return null;

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: 400 },
        minWidth: { xs: "100%", sm: 400 },
        maxWidth: { xs: "100%", sm: 400 },
        height: isLargeScreen ? `calc(100vh - ${totalTopOffsetPx}px)` : "100vh",
        borderLeft: isLargeScreen ? `1px solid ${INPUT_COLOR_BORDER}` : "none",
        display: isVisible ? "flex" : "none",
        flexDirection: "column",
        backgroundColor: "background.paper",
        position: isLargeScreen ? "relative" : "fixed",
        top: isLargeScreen ? "auto" : rebrandBannerOffsetPx,
        right: isLargeScreen ? "auto" : 0,
        bottom: isLargeScreen ? "auto" : 0,
        left: isLargeScreen ? "auto" : "auto",
        zIndex: isLargeScreen ? "auto" : 1299,
        boxShadow: isLargeScreen ? 0 : 8,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 5,
          right: 15,
          zIndex: 2001,
          display: isLargeScreen ? "none" : "block",
        }}
      >
        <IconButton
          onClick={onCloseChat}
          sx={{
            backgroundColor: "background.paper",
            border: `1px solid ${INPUT_COLOR_BORDER}`,
          }}
        >
          <X size={17} />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isLargeScreen ? "flex-end" : "flex-start",
          pr: 2,
          pl: 1,
          pt: isLargeScreen ? 1.5 : 1,
          backgroundColor: "background.paper",
          position: "relative",
        }}
      >
        <IconButton
          size="small"
          onClick={onResetChat}
          sx={{
            pb: 0,
            pt: isLargeScreen ? 1 : "5px",
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <CampaignOpeningAssistProvider
          highlightMessageId={highlightMessageKey}
          emptyChatPlaceholder={emptyChatPlaceholder}
        >
          <CommonChatMainContainer
            key={resetChatKey}
            statisticsView={false}
            embedded
            hideLauncher
            showOnlyMessages
            controlledOpen={isOpenChat}
            onChangeOpen={setIsOpenChat}
            exposeSendMessage={onExposeSendMessage}
            exposeChatActions={onExposeChatActions}
            onAgentReply={onAgentReply}
            sx={{
              position: "relative",
              bottom: 0,
              right: 0,
              width: "100%",
              height: "100%",
              boxShadow: 0,
              border: 0,
            }}
            contentSx={{
              minHeight: "100%",
              maxHeight: "100%",
              width: "100%",
              height: "100%",
            }}
            showMinimize={false}
            welcomeCenteredSingleColumn={true}
            optimizationsSingleColumn={true}
            showHeader={false}
            chatConfigurationID={CHAT_CONFIGURATION_ADS_DETAILED_REPORT_ID}
            chatContext={{
              campaign: getFormattedCampaign({
                data: {
                  ...campaign,
                  historicalStatistics: statistics,
                },
                format: "save",
              }),
              reportConfiguration: {
                startDateTime: dateRange?.startDateTime || null,
                endDateTime: dateRange?.endDateTime || null,
              },
            }}
            onCallbackChangeOptimizations={onChangeOptimizations}
            onRegisterClearPreloadedMessages={onRegisterClearPreloadedMessages}
            startMode="messageInput"
            placeholderInput={CHAT_ASK_A_QUESTION_ABOUT_YOUR_CAMPAIGN}
            headerTitle={CHAT_ASK_A_QUESTION_ABOUT_YOUR_CAMPAIGN}
            preloadedMessages={chatMessages}
            disabledInteractions={isSyncingData}
          />
        </CampaignOpeningAssistProvider>
      </Box>

      <Box
        sx={{
          px: 1.5,
          pt: 0.2,
          pb: 1.5,
          backgroundColor: "background.paper",
        }}
      >
        <CommonChatMessageInput
          as="MessageInput"
          disabled={isSyncingData}
          embedded={true}
          onSendMessage={(text) => {
            if (isSyncingData) {
              return false;
            }
            return chatSendHandlerRef.current?.(text);
          }}
          showSendFileButton={false}
          placeholderInput={CHAT_ASK_A_QUESTION_ABOUT_YOUR_CAMPAIGN}
          multiline={true}
        />
      </Box>
    </Box>
  );
};

export default SlideChat;
